import type { JourneyPage, JourneyJson } from "./quoteJourneyState";

export const LEGACY_NUMBER_FIELDS = [
  "total_s",
  "n_tool_changes",
  "stock_mass_kg",
  "removed_volume_mm3",
  "spindle_power_w",
] as const;
export type LegacyNumberField = (typeof LEGACY_NUMBER_FIELDS)[number];
export type LegacyQuoteValues = Record<LegacyNumberField, string> & {
  material: string;
  rate_card_id: string | null;
  part_id: string;
};
export type LegacyReviewDraft = {
  quoteId: string;
  note: string;
  open: boolean;
};
export type LegacyNewJourney = {
  values: LegacyQuoteValues;
  pickedProgramId: string | null;
  createdQuoteId: string | null;
  reviewDraft: LegacyReviewDraft | null;
};
export type LegacyHistoryJourney = {
  statusFilter: "draft" | "pending_review" | "approved" | "rejected" | null;
  showTaxInc: boolean;
  page: number;
  drawerQuoteId: string | null;
  reviewDraft: LegacyReviewDraft | null;
};
export type LegacyJourneyView = {
  activeTab: "new" | "history" | "rate-cards";
  new: LegacyNewJourney;
  history: LegacyHistoryJourney;
};
const uuid = (v: unknown): v is string =>
  typeof v === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const nullableId = (v: unknown) => v === null || uuid(v);
const text = (v: unknown, limit: number): v is string =>
  typeof v === "string" && v.length <= limit;
function object(v: unknown): v is Record<string, unknown> {
  return (
    v !== null &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    [Object.prototype, null].includes(Object.getPrototypeOf(v))
  );
}
function keys(v: Record<string, unknown>, allowed: readonly string[]) {
  return (
    Object.keys(v).length === allowed.length &&
    Object.keys(v).every((key) => allowed.includes(key))
  );
}
function review(v: unknown, quoteId: unknown): v is LegacyReviewDraft | null {
  return (
    v === null ||
    (object(v) &&
      keys(v, ["quoteId", "note", "open"]) &&
      uuid(v.quoteId) &&
      v.quoteId === quoteId &&
      text(v.note, 4000) &&
      typeof v.open === "boolean")
  );
}
export function readLegacyJourney(
  page: JourneyPage | null,
): LegacyJourneyView | null {
  if (page?.key !== "legacy" || page.version !== 1 || page.files.length)
    return null;
  const v = page.data;
  if (
    !keys(v, ["activeTab", "new", "history"]) ||
    typeof v.activeTab !== "string" ||
    !["new", "history", "rate-cards"].includes(v.activeTab) ||
    !object(v.new) ||
    !object(v.history)
  )
    return null;
  const n = v.new,
    h = v.history;
  if (
    !keys(n, ["values", "pickedProgramId", "createdQuoteId", "reviewDraft"]) ||
    !object(n.values) ||
    !nullableId(n.pickedProgramId) ||
    !nullableId(n.createdQuoteId) ||
    !review(n.reviewDraft, n.createdQuoteId)
  )
    return null;
  const values = n.values;
  if (
    !keys(values, [
      ...LEGACY_NUMBER_FIELDS,
      "material",
      "rate_card_id",
      "part_id",
    ]) ||
    !LEGACY_NUMBER_FIELDS.every((key) => text(values[key], 100)) ||
    !text(values.material, 4000) ||
    !text(values.part_id, 256) ||
    !nullableId(values.rate_card_id)
  )
    return null;
  if (
    !keys(h, [
      "statusFilter",
      "showTaxInc",
      "page",
      "drawerQuoteId",
      "reviewDraft",
    ]) ||
    !(
      h.statusFilter === null ||
      (typeof h.statusFilter === "string" &&
        ["draft", "pending_review", "approved", "rejected"].includes(
          h.statusFilter,
        ))
    ) ||
    typeof h.showTaxInc !== "boolean" ||
    !Number.isSafeInteger(h.page) ||
    Number(h.page) < 1 ||
    !nullableId(h.drawerQuoteId) ||
    !review(h.reviewDraft, h.drawerQuoteId)
  )
    return null;
  return v as unknown as LegacyJourneyView;
}
export function legacyJourneyData(
  view: LegacyJourneyView,
): Record<string, JourneyJson> {
  return JSON.parse(JSON.stringify(view));
}
export function emptyLegacyNewJourney(): LegacyNewJourney {
  return {
    values: {
      total_s: "0",
      n_tool_changes: "0",
      stock_mass_kg: "0",
      removed_volume_mm3: "0",
      spindle_power_w: "0",
      material: "",
      rate_card_id: null,
      part_id: "",
    },
    pickedProgramId: null,
    createdQuoteId: null,
    reviewDraft: null,
  };
}
